import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  input,
  output,
  signal,
  viewChild
} from "@angular/core";
import { FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";

import { CustomIconComponent } from "@shared/components/custom-icon/custom-icon.component";
import { CustomIconName } from "@shared/icons/icon-definitions";

import { BaseControlValueAccessorComponent } from "../base/base-control-value-accessor.component";

@Component({
  selector: "component-input",
  templateUrl: "./input.component.html",
  styleUrl: "./input.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CustomIconComponent, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true
    }
  ]
})
export class InputComponent extends BaseControlValueAccessorComponent<string> {
  readonly type = input<"text" | "email" | "password">("text");
  readonly label = input<string>("");
  readonly placeholder = input<string>("");
  readonly errorMessage = input<string | null>(null);
  readonly isClearable = input<boolean>(false);
  readonly prefixIcon = input<CustomIconName | null>(null);
  readonly suffixIcon = input<CustomIconName | null>(null);
  readonly isFocused = input(false, { transform: booleanAttribute });
  readonly size = input<"sm" | "md" | "lg">("md");
  readonly floating = input(true, { transform: booleanAttribute }); // true = floating label, false = regular label
  readonly secondary = input(false, { transform: booleanAttribute }); // true = secondary input, false = primary input

  readonly inputHtmlElement = viewChild<ElementRef<HTMLInputElement>>("inputField");

  readonly valueChange = output<string>();
  readonly blur = output<void>();
  readonly enter = output<void>();

  private static _nextId = 0;
  protected readonly inputId = computed(() => `input-${InputComponent._nextId++}`);

  private readonly _inputElement = viewChild<ElementRef<HTMLInputElement>>("inputField");

  // Computed properties (must be declared before constructor for member ordering)
  protected readonly hasError = computed(() => !!this.errorMessage());
  protected readonly showPassword = signal(false);
  protected readonly hasValue = computed(() => this._value() && this._value()!.length > 0);
  protected readonly showClearButton = computed(() => this.isClearable() && this.hasValue() && !this._disabled());
  protected readonly isPasswordType = computed(() => this.type() === "password");
  protected readonly inputType = computed(() => {
    if (this.type() === "password" && this.showPassword()) {
      return "text";
    }
    return this.type();
  });
  protected readonly visibilityIcon = computed<CustomIconName>(() => {
    return this.showPassword() ? "eyeOff" : "eye";
  });
  protected readonly hasActions = computed(() => {
    return this.showClearButton() || this.isPasswordType() || !!this.suffixIcon();
  });

  constructor() {
    super();

    // Auto-focus when isFocused becomes true
    effect(() => {
      if (this.isFocused()) {
        // Use a timeout to ensure the input element is available
        this.focus();
      }
    });
  }

  focus(): void {
    setTimeout(() => {
      if (this._inputElement()) {
        this._inputElement()?.nativeElement.focus();
      }
    }, 0);
  }

  protected onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const newValue = target.value;

    // Update internal value
    this._value.set(newValue);

    // Notify Angular Forms (for formControl)
    this.onChange(newValue);

    // Emit for direct value binding
    this.valueChange.emit(newValue);
  }

  protected onBlur(): void {
    // Mark as touched for form validation
    this.markAsTouched();
    this.blur.emit();
  }

  protected onKeyDown(event: KeyboardEvent): void {
    if (event.key === "Enter") {
      this.enter.emit();
    }
  }

  protected toggleVisibility(): void {
    this.showPassword.update((value) => !value);
  }

  protected onClear(): void {
    // Update internal value
    this._value.set("");

    // Notify Angular Forms
    this.onChange("");

    // Emit for direct value binding
    this.valueChange.emit("");
  }
}
